"use client";

// Carrusel autoplay de las 6 features — traducción a React del mecanismo
// vanilla-JS documentado en el handoff (README "Sección 5"): las 6 features
// comparten un mismo "stage" superpuesto, rotan una a la vez con
// requestAnimationFrame (no setInterval), barra de segmentos con fill
// progresivo + click-to-jump, flechas prev/next con loop, pausa en
// hover/fuera de viewport, respeta prefers-reduced-motion.

import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight, Check } from "lucide-react";
import { L, SHADOWS } from "./tokens";

const STAGE_DUR = 5600;
const STAGE_LABELS = ["Pipeline", "Análisis IA", "Calendario", "Búsquedas", "Portal", "Reportes"];

type Feature = {
  title: React.ReactNode;
  lead: string;
  bullets: string[];
  image: string;
  alt: string;
  frameUrl: string;
  tag?: string;
};

const FEATURES: Feature[] = [
  {
    title: <>Un sistema.<br /><em style={{ fontStyle: "normal", color: L.inkFaint }}>Una pantalla.</em></>,
    lead: "Cada candidato entra al pipeline, se rankea por fit y avanza por etapa. Sin planillas paralelas.",
    bullets: [
      "**Drag-and-drop** entre etapas configurables por búsqueda.",
      "Filtrado por **fit IA, fuente o reclutador** en un click.",
      "Cada tarjeta muestra **score, fuente y días en etapa** de un vistazo.",
    ],
    image: "/landing/pipeline-premium.png",
    alt: "Pipeline kanban con candidatos rankeados por fit en etapas configurables",
    frameUrl: "talenv.com / pipeline · Senior Product Manager",
  },
  {
    title: <>Las primeras 3 horas se vuelven <span style={{ color: L.violet }}>5 minutos.</span></>,
    lead: "Subís el CV una vez. Talenv extrae los datos, matchea contra el perfil y rankea al candidato — con evidencia textual.",
    bullets: [
      "**Extracción automática** de skills, experiencia, último puesto, educación e idiomas.",
      "**Keyword matching con evidencia**: ves la frase del CV donde dice cada cosa.",
      "**Score 0–100** desglosado por dimensión.",
      "Resumen breve, fortalezas y brechas claras — listo para compartir con el hiring manager.",
    ],
    image: "/landing/perfil-candidato-premium.png",
    alt: "Análisis IA de un candidato con desglose por dimensiones y fortalezas detectadas",
    frameUrl: "talenv.com / candidato · Lucía Ferreyra",
    tag: "Match 94 · 5/5 excelente fit",
  },
  {
    title: <>Tu semana,<br /><em style={{ fontStyle: "normal", color: L.inkFaint }}>en una sola vista.</em></>,
    lead: "Agendá entrevistas, sumá panelistas y mandá invitaciones desde Talenv. Sin ir y volver entre el ATS, el mail y el calendario.",
    bullets: [
      "**Sincronización con Google Calendar** y Outlook — la entrevista aparece en tu agenda y la del candidato.",
      "Sumá **panelistas y el link de videollamada** en la misma vista — todos reciben la invitación con un click.",
      "**Recordatorios y reprogramaciones** sin perderte de ninguna confirmación.",
    ],
    image: "/landing/calendario-premium.png",
    alt: "Calendario de entrevistas en Talenv",
    frameUrl: "www.talenv.com / calendario",
  },
  {
    title: <>Todas tus búsquedas,<br /><em style={{ fontStyle: "normal", color: L.inkFaint }}>en un solo lugar.</em></>,
    lead: "Abrís, cerrás y compartís cada búsqueda desde una pantalla. Estado, dueño, postulaciones y fechas, sin perderte de nada.",
    bullets: [
      "**Estados claros**: borrador, publicada, en pausa o cerrada — con responsable asignado.",
      "**Compartir con un solo click** — link, copy y QR generados para LinkedIn, WhatsApp y mail.",
      "**Filtros por área, modalidad y ubicación** para encontrar cualquier vacante en segundos.",
    ],
    image: "/landing/vacantes-premium.png",
    alt: "Búsquedas activas en Talenv: estado, responsable y postulaciones por vacante",
    frameUrl: "talenv.com / busquedas",
  },
  {
    title: <>Tu portal,<br /><em style={{ fontStyle: "normal", color: L.inkFaint }}>tu marca.</em></>,
    lead: "Publicás todas tus búsquedas activas en un sitio público con tu logo, colores y mensaje. Las postulaciones entran directo al pipeline.",
    bullets: [
      "**Customización completa**: logo, paleta y mensaje de bienvenida.",
      "**Portal propio** con URL pública para compartir en cualquier canal.",
      "**Filtros por modalidad y ubicación** para que los candidatos encuentren su match.",
    ],
    image: "/landing/portal-premium.png",
    alt: "Portal de empleos con la marca de la empresa",
    frameUrl: "www.talenv.com / portal / nortalia",
  },
  {
    title: <>Métricas que se calculan <span style={{ color: L.violet }}>solas.</span></>,
    lead: "Funnel, time-to-hire, fuente, conversión por etapa. Todo filtrable por puesto y ventana temporal. Sin armar nada en Excel.",
    bullets: [
      "**Funnel de conversión** etapa-a-etapa por búsqueda o agregado.",
      "Time-to-hire **mediana, promedio y P90**.",
      "**Efectividad por fuente** y por reclutador.",
      "Exportable a **CSV y PDF** para reportes mensuales.",
    ],
    image: "/landing/reportes-premium.png",
    alt: "Reportes con KPIs, candidatos por semana, fuente y funnel de conversión",
    frameUrl: "talenv.com / reportes",
  },
];

function renderBullet(text: string) {
  const parts = text.split(/\*\*(.+?)\*\*/g);
  return parts.map((part, i) => (i % 2 === 1 ? <b key={i}>{part}</b> : <span key={i}>{part}</span>));
}

export function FeaturesCarousel() {
  const [idx, setIdx] = useState(0);
  const [fills, setFills] = useState<number[]>(() => FEATURES.map(() => 0));
  const sectionRef = useRef<HTMLDivElement>(null);
  const hoverRef = useRef(false);
  const onScreenRef = useRef(true);
  const elapsedRef = useRef(0);
  const lastRef = useRef(0);
  const rafRef = useRef<number | null>(null);
  const idxRef = useRef(0);
  const shotRef = useRef<HTMLDivElement>(null);

  // tilt: inclinación 3D del screenshot al mover el mouse encima (manipula
  // el DOM directo por ref — evita un re-render de React en cada mousemove).
  const handleTiltMove = useCallback((e: React.MouseEvent<HTMLDivElement>) => {
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    const el = shotRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    const x = (e.clientX - r.left) / r.width - 0.5;
    const y = (e.clientY - r.top) / r.height - 0.5;
    el.style.transition = "transform .12s ease-out";
    el.style.transform = `perspective(900px) rotateX(${(-y * 5).toFixed(2)}deg) rotateY(${(x * 6).toFixed(2)}deg)`;
  }, []);
  const handleTiltLeave = useCallback(() => {
    const el = shotRef.current;
    if (!el) return;
    el.style.transition = "transform .5s cubic-bezier(.22,1,.36,1)";
    el.style.transform = "";
  }, []);

  const goTo = useCallback((i: number) => {
    const n = FEATURES.length;
    const next = ((i % n) + n) % n;
    idxRef.current = next;
    elapsedRef.current = 0;
    lastRef.current = 0;
    setIdx(next);
    setFills(FEATURES.map((_, k) => (k < next ? 100 : 0)));
  }, []);

  useEffect(() => {
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) return;

    function frame(ts: number) {
      if (lastRef.current === 0) lastRef.current = ts;
      const dt = ts - lastRef.current;
      lastRef.current = ts;
      if (!hoverRef.current && onScreenRef.current) {
        elapsedRef.current += dt;
        const p = Math.min(elapsedRef.current / STAGE_DUR, 1);
        setFills((prev) => {
          const next = [...prev];
          next[idxRef.current] = p * 100;
          return next;
        });
        if (p >= 1) goTo(idxRef.current + 1);
      }
      rafRef.current = requestAnimationFrame(frame);
    }
    rafRef.current = requestAnimationFrame(frame);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [goTo]);

  useEffect(() => {
    const el = sectionRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => { entries.forEach((e) => { onScreenRef.current = e.isIntersecting; if (e.isIntersecting) lastRef.current = 0; }); },
      { threshold: 0 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const active = FEATURES[idx];

  return (
    <section
      id="features"
      ref={sectionRef}
      onMouseEnter={() => { hoverRef.current = true; }}
      onMouseLeave={() => { hoverRef.current = false; lastRef.current = 0; }}
      style={{ background: idx % 2 === 0 ? L.bg : L.bgWarm, padding: "110px 0", transition: "background .4s ease" }}
    >
      {/* Barra de segmentos */}
      <div className="mx-auto mb-7 flex max-w-[1100px] items-stretch gap-2 px-8">
        {FEATURES.map((_, i) => (
          <button
            key={i}
            type="button"
            onClick={() => goTo(i)}
            className="flex flex-1 flex-col items-start gap-1.5"
          >
            <span className="h-[3px] w-full overflow-hidden rounded-full" style={{ background: "rgba(21,16,61,.12)" }}>
              <i className="block h-full rounded-full" style={{ width: `${fills[i]}%`, background: L.violet, transition: fills[i] === 0 ? "none" : undefined }} />
            </span>
            <span
              className="hidden [@media(min-width:760px)]:inline"
              style={{ fontFamily: L.fontMono, fontSize: 10.5, fontWeight: 600, color: i === idx ? L.ink : L.inkFaint, transition: "color 200ms" }}
            >
              {i < 9 ? "0" : ""}{i + 1} · {STAGE_LABELS[i]}
            </span>
          </button>
        ))}
      </div>

      {/* Stage */}
      <div className="relative mx-auto max-w-[1240px] px-8">
        <button
          type="button"
          onClick={() => goTo(idx - 1)}
          aria-label="Anterior"
          className="absolute left-0 top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border bg-white transition-colors sm:flex"
          style={{ borderColor: L.line, color: L.ink }}
          onMouseEnter={(e) => { e.currentTarget.style.background = L.violet; e.currentTarget.style.color = "#fff"; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = L.ink; }}
        >
          <ChevronLeft size={18} />
        </button>
        <button
          type="button"
          onClick={() => goTo(idx + 1)}
          aria-label="Siguiente"
          className="absolute right-0 top-1/2 z-10 hidden h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full border bg-white transition-colors sm:flex hover:text-white"
          style={{ borderColor: L.line, color: L.ink }}
          onMouseEnter={(e) => { e.currentTarget.style.background = L.violet; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = "#fff"; e.currentTarget.style.color = L.ink; }}
        >
          <ChevronRight size={18} />
        </button>

        <div className={`grid grid-cols-1 items-center gap-10 lg:grid-cols-[0.85fr_1.15fr] ${idx % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
          <div key={`text-${idx}`} className="tl-fade-in">
            <h2 style={{ fontFamily: L.fontDisplay, fontWeight: 800, letterSpacing: "-0.025em", lineHeight: 1.02, fontSize: "clamp(36px, 4.4vw, 52px)", color: L.ink, margin: "0 0 16px" }}>
              {active.title}
            </h2>
            <p style={{ fontSize: 17, lineHeight: 1.5, color: L.inkSoft, maxWidth: 520, margin: "0 0 22px" }}>{active.lead}</p>
            <ul className="flex flex-col gap-2.5">
              {active.bullets.map((b, i) => {
                const liDelay = 150 + i * 200;
                const checkDelay = 300 + i * 200;
                return (
                  <li
                    key={i}
                    className="tl-bullet-in flex items-start gap-2.5"
                    style={{ ["--bd" as string]: `${liDelay}ms` }}
                  >
                    <span
                      className="tl-check-in mt-0.5 flex h-[22px] w-[22px] flex-shrink-0 items-center justify-center rounded-full"
                      style={{ background: L.violetSoft, color: L.violet, ["--cd" as string]: `${checkDelay}ms` }}
                    >
                      <Check size={12} strokeWidth={3} />
                    </span>
                    <span style={{ fontSize: 14.5, color: L.ink2, lineHeight: 1.5 }}>{renderBullet(b)}</span>
                  </li>
                );
              })}
            </ul>
          </div>

          <div key={`shot-${idx}`} className="tl-fade-in relative">
            <div
              ref={shotRef}
              onMouseMove={handleTiltMove}
              onMouseLeave={handleTiltLeave}
              className="overflow-hidden rounded-2xl border bg-white"
              style={{
                borderColor: L.line,
                // featGlow: sombra de color alternada por índice par/impar.
                boxShadow: idx % 2 === 0
                  ? "0 40px 90px -30px rgba(107,92,255,.45), 0 10px 30px -12px rgba(107,92,255,.25)"
                  : "0 40px 90px -30px rgba(164,205,20,.4), 0 10px 30px -12px rgba(120,150,30,.22)",
              }}
            >
              <div className="flex items-center gap-2.5 px-4 py-2.5" style={{ background: "#F1EEE7", borderBottom: `1px solid ${L.line}` }}>
                <div className="flex gap-1.5">
                  <i className="h-2 w-2 rounded-full" style={{ background: "#FF5F57" }} />
                  <i className="h-2 w-2 rounded-full" style={{ background: "#FEBC2E" }} />
                  <i className="h-2 w-2 rounded-full" style={{ background: "#28C840" }} />
                </div>
                <div style={{ fontFamily: L.fontMono, fontSize: 11, color: L.inkSoft, overflow: "hidden" }}>
                  <span className="tl-url-type inline-block whitespace-nowrap align-bottom">{active.frameUrl}</span>
                </div>
              </div>
              <Image src={active.image} alt={active.alt} width={1100} height={720} className="block h-auto w-full" />
              {/* featCursor: cursor fantasma "usando" el producto — solo en
                  la primera feature (Pipeline), igual que `featShots[0]`. */}
              {idx === 0 && (
                <div className="tl-ghost-cursor pointer-events-none absolute" style={{ zIndex: 8, width: 18, height: 18, filter: "drop-shadow(0 2px 4px rgba(0,0,0,.3))" }}>
                  <svg viewBox="0 0 24 24" width={18} height={18}><path d="M4 2l16 8-7 2-3 7z" fill="#15103D" stroke="white" strokeWidth={1.4} /></svg>
                </div>
              )}
            </div>
            {active.tag && (
              <div
                className="tl-callout-pop absolute flex items-center gap-2 rounded-xl bg-white px-3 py-2"
                style={{ bottom: 24, left: 24, boxShadow: SHADOWS.cardSm }}
              >
                <span style={{ color: L.lime, background: L.ink, borderRadius: 999, width: 20, height: 20, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11 }}>✦</span>
                <span style={{ fontSize: 12.5, color: L.ink }}><b>{active.tag.split(" · ")[0]}</b> · {active.tag.split(" · ")[1]}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      <style
        dangerouslySetInnerHTML={{
          __html: `
@keyframes tl-fade-in { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: none; } }
.tl-fade-in { animation: tl-fade-in .5s cubic-bezier(.22,1,.36,1); }

/* featBullets */
.tl-bullet-in { opacity: 0; transform: translateX(-10px); animation: tl-bullet-in .45s cubic-bezier(.22,1,.36,1) forwards; animation-delay: var(--bd, 0ms); }
@keyframes tl-bullet-in { to { opacity: 1; transform: none; } }
.tl-check-in { transform: scale(0); animation: tl-check-in .35s cubic-bezier(.34,1.56,.64,1) forwards; animation-delay: var(--cd, 0ms); }
@keyframes tl-check-in { to { transform: scale(1); } }

/* featUrlType */
.tl-url-type { max-width: 0; animation: tl-type 1.2s steps(30) .3s forwards; }
@keyframes tl-type { to { max-width: 100%; } }

/* featCallouts */
.tl-callout-pop { opacity: 0; transform: translateY(10px) scale(.9); animation: tl-callout-pop .55s cubic-bezier(.34,1.56,.64,1) .15s forwards; }
@keyframes tl-callout-pop { to { opacity: 1; transform: none; } }

/* featCursor — recorrido decorativo en loop sobre el primer feature-shot */
.tl-ghost-cursor { animation: tl-curmove 9s ease-in-out infinite; }
@keyframes tl-curmove { 0%, 100% { left: 22%; top: 30%; } 22% { left: 60%; top: 38%; } 28% { left: 60%; top: 38%; } 50% { left: 38%; top: 66%; } 56% { left: 38%; top: 66%; } 80% { left: 74%; top: 56%; } 86% { left: 74%; top: 56%; } }

@media (prefers-reduced-motion: reduce) {
  .tl-fade-in, .tl-bullet-in, .tl-check-in, .tl-url-type, .tl-callout-pop, .tl-ghost-cursor { animation: none !important; opacity: 1 !important; transform: none !important; max-width: 100% !important; }
  .tl-ghost-cursor { display: none !important; }
}
`,
        }}
      />
    </section>
  );
}
